import {
  getAllIds,
  getCountByStatus,
  useGetAllMultiplePrints,
} from "@/api/multipleprint/multipleprint";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import NewMultiprintDialog from "@/components/multiprint/NewMultiprintDialog";
import MultiplePrintDrawer from "@/components/multipleprint/MultiplePrintDrawer";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DashboardListGridLayout } from "@/components/dashboard/DashboardListGridLayout";
import { GridCardWithActions } from "@/components/links/GridCardWithActions";
import { MultipleprintLinkGridCard } from "@/components/links/relatedLinkGridCards";
import { DashboardGridCardActions } from "@/components/table/DashboardGridCardActions";
import { DataTable } from "@/components/ui/datatable";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  multipleprintsAdvancedSearchConfig,
  multipleprintsColumns,
} from "@/config/dashboard/multipleprints";
import DashboardSelectionBar from "@/components/table/DashboardSelectionBar";
import { useDashboardListScrollContext } from "@/hooks/useDashboardListScrollContext";
import { useDashboardActionsColumn } from "@/hooks/useDashboardActionsColumn";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useDashboardRowSelection } from "@/hooks/useDashboardRowSelection";
import { Multipleprint } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ACTIONS_COLUMN_ID,
  ensureActionsColumnLast,
} from "@/components/table/table-actions-column";

const STORAGE_KEY = "multiprint-table-preferences";

const flatVisibleMultipleprintColumns = [
  ...multipleprintsColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const MultiprintDashboard = () => {
  const {
    pagination,
    setPagination,
    countByStatus,
    currentCount,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Multipleprint>({
    resourceName: "multipleprint",
    resourceCounter: getCountByStatus,
    statusField: "estampesMultiplesPublicationStatus",
  });

  const { t } = useTranslation();

  const { data, isFetching } = useGetAllMultiplePrints(fetchParams);
  const {
    listKey,
    navigateToFiche,
    entityIds,
    isLoading: listScrollLoading,
    listPagination,
  } = useDashboardListScrollContext(data, pagination, isFetching, STORAGE_KEY);

  const actionsColumn = useDashboardActionsColumn<Multipleprint>({
    documentType: "multipleprint",
    publicationStatusField: "estampesMultiplesPublicationStatus",
    onEdit: (row) =>
      navigateToFiche(`/edit/multipleprint/${row.id}`, row.id),
  });

  const columns: ColumnDef<Multipleprint, any>[] = useMemo(() => {
    return [...generateTable(multipleprintsColumns, "multipleprint"), actionsColumn];
  }, [actionsColumn]);


  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      const { order } = saved;
      return ensureActionsColumnLast(order || flatVisibleMultipleprintColumns);
    }
    return flatVisibleMultipleprintColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...multipleprintsColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...multipleprintsColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  const {
    selectionState,
    setSelectionState,
    dataTableSelection,
    isSelected,
    setRowSelected,
  } = useDashboardRowSelection<Multipleprint>({
      getAllRowIds: () => getAllIds(fetchParams.where),
    });

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("entryMultipleprints")}</p>
        <GlobalSearch resource="multipleprints"
          config={multipleprintsAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={multipleprintsAdvancedSearchConfig}
            resource="multipleprints"
          />
          <AdvancedSort config={multipleprintsAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createNewmultipleprint")}
            Dialog={NewMultiprintDialog}
            batchImportType="multipleprint"
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DashboardListGridLayout
          storageKey={STORAGE_KEY}
          listKey={listKey}
          entityIds={entityIds}
          isLoading={listScrollLoading}
          listPagination={listPagination}
          setListPagination={setPagination}
          listTable={
            <DataTable
              onRowClick={(row) =>
                navigateToFiche(
                  `/edit/multipleprint/${row.original.id}`,
                  row.original.id,
                )
              }
              getListItemId={(row) => row.original.id}
              pagination={{
                pagination: pagination,
                setPage: setPagination,
                totalSize: currentCount,
              }}
              storageKey={STORAGE_KEY}
              columns={columns}
              data={data ?? []}
              options={{
                enableColumnResizing: true,
                state: {
                  columnVisibility: columnVisibility,
                  columnOrder: columnsOrder,
                },
                onColumnVisibilityChange: setColumnVisibility,
                onColumnOrderChange: setColumnsOrder,
              }}
              selection={dataTableSelection}
            />
          }
          gridContent={
            <>
              {(data ?? []).map((item) => (
                <GridCardWithActions
                  key={item.id}
                  selected={isSelected(item.id)}
                  onSelectedChange={(checked) =>
                    setRowSelected(item.id, checked)
                  }
                  actions={
                    <DashboardGridCardActions
                      documentType="multipleprint"
                      item={item}
                      publicationStatusField="estampesMultiplesPublicationStatus"
                      onEdit={(row) =>
                        navigateToFiche(`/edit/multipleprint/${row.id}`, row.id)
                      }
                    />
                  }
                >
                  <MultipleprintLinkGridCard
                    item={item}
                    t={t}
                    onTextClick={() =>
                      navigateToFiche(`/edit/multipleprint/${item.id}`, item.id)
                    }
                  />
                </GridCardWithActions>
              ))}
            </>
          }
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
        />
        <DashboardSelectionBar
          documentType="multipleprint"
          selectionState={selectionState}
          setSelectionState={setSelectionState}
        >
          {(selectedIds) => (
            <MultiplePrintDrawer multipleprintIds={selectedIds} />
          )}
        </DashboardSelectionBar>
      </div>
    </div>
  );
};

export default MultiprintDashboard;
